import React, { useEffect, useState } from "react";
import ApexChart from "react-apexcharts";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import axios from "axios";
import { getHeadersForRequest } from "../../utils/functions";
import {
  Button,
  Card,
  Collapse,
  HStack,
  Stack,
  Text,
  useBreakpointValue,
  useColorModeValue,
} from "@chakra-ui/react";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";

dayjs.extend(isoWeek);

interface ProductionEntry {
  date: string;
  finishedMaterialWeight: string;
  burntWeight: string;
}

const DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const ProductionHeatmapApex = () => {
  const [series, setSeries] = useState<any[]>([]);

  const [show, setShow] = useState<boolean>(false);

  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "whiteAlpha.900");

  const neutral = useColorModeValue("#e5e7eb", "#4A5568"); // gray-200 vs gray-600
  const light = useColorModeValue("#63b3ed", "#4299e1");
  const medium = useColorModeValue("#3182ce", "#3182ce");
  const dark = useColorModeValue("#2c5282", "#2b6cb0");
  const darker = useColorModeValue("#1A365D", "#2a4365");

  const tooltipBgColor = useColorModeValue("#ffffff", "#1A202C");
  const tooltipTextColor = useColorModeValue("#121721", "#E2E8F0");
  const strokeColor = useColorModeValue("#ffffff", "#1A202C"); // gray.700 / white

  const isDark = useColorModeValue(false, true);
  const axisTextColor = isDark ? "#E2E8F0" : "#1A202C"; // gray.100 / gray.900

  const weeks = series[0]?.data.length || 26;
  const cellSize = 20;
  const chartWidth = weeks * cellSize;
  const chartHeight = 7 * cellSize;

  const isSmallScreen = useBreakpointValue({ base: true, md: false }); // < md
  const isMediumScreen = useBreakpointValue({ base: false, md: true, xl: false }); // md & lg only
  const isLargeScreen = useBreakpointValue({ base: false, xl: true }); // ≥ xl
  

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = await getHeadersForRequest();
        const res = await axios.get(
          `${process.env.REACT_APP_TEST_API_URL}/production/getAll`,
          { headers }
        );
        const data = res?.data?.data || res?.data || [];
        const series = transformToHeatmapSeries(data);
        setSeries(series);
      } catch (err) {
        console.error("Error fetching production data:", err);
      }
    };
    fetchData();
  }, []);

  const transformToHeatmapSeries = (data: ProductionEntry[]) => {
    const weekMap: Record<
      string,
      Record<number, { value: number; date: string }>
    > = {};
    const today = dayjs();
    const startDate = today.subtract(6, "month").startOf("week");
    const totalDays = today.diff(startDate, "day") + 1;

    for (let i = 0; i < totalDays; i++) {
      const d = startDate.add(i, "day");
      const dateStr = d.format("DD MMM YYYY");
      const weekKey = d.startOf("week").format("DD MMM YYYY");
      const weekday = d.day();
    
      const entries = data.filter(
        (e) => dayjs(e.date).format("DD MMM YYYY") === dateStr
      );
      const net = entries.reduce((sum, e) => {
        const finished = parseFloat(e.finishedMaterialWeight) || 0;
        const burnt = parseFloat(e.burntWeight) || 0;
        return sum + (finished - burnt);
      }, 0);
    
      if (!weekMap[weekKey]) weekMap[weekKey] = {};
      weekMap[weekKey][weekday] = { value: net, date: dateStr };
    }

    const weeks = Object.keys(weekMap).sort(
      (a, b) => dayjs(a).unix() - dayjs(b).unix()
    );

    return [...DAYS].reverse().map((day, dayIndexReversed) => {
      const dayIndex = 6 - dayIndexReversed;
      return {
        name: day,
        data: weeks.map((weekKey) => {
          const startOfWeek = dayjs(weekKey);
          const currentDate = startOfWeek.add(dayIndex, "day");
          const dateStr = currentDate.format("YYYY-MM-DD");

          const cell = weekMap[weekKey]?.[dayIndex];
          return {
            x: weekKey,
            y: cell ? cell.value : 0,
            custom: cell ? cell.date : dateStr,
          };
        }),
      };
    });
  };

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: "heatmap",
      toolbar: { show: false },
      background: bgColor,
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      labels: { show: false },
      tooltip: { enabled: false },
    },
    stroke: {
      width: isSmallScreen ? 4 : 10,
      colors: [strokeColor],
    },
    yaxis: {
      labels: {
        show: !isSmallScreen,
        style: {
          colors: axisTextColor,
        },
        formatter: (_, index) => DAYS[6 - index] ?? "",
      },
    },
    tooltip: {
      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        const point = w.config.series[seriesIndex].data[dataPointIndex];
        const date = point.custom;
        const value = point.y;

        return `
            <div style="
              background-color: ${tooltipBgColor};
              color: ${tooltipTextColor};
              padding: 8px;
              border-radius: 6px;
              font-size: 13px;
              box-shadow: 0 2px 6px rgba(0,0,0,0.2);
            ">
              ${date}<br/><strong>${value.toFixed(2)} kg</strong>
            </div>
          `;
      },
    },
    plotOptions: {
      heatmap: {
        enableShades: false,
        radius: isSmallScreen ? 5 : 10,
        useFillColorAsStroke: false,
        colorScale: {
          ranges: [
            { from: 0, to: 0, color: neutral },
            { from: 0.01, to: 4.99, color: light },
            { from: 5, to: 9.99, color: medium },
            { from: 10, to: 19.99, color: dark },
            { from: 20, to: 1000, color: darker },
          ],
        },
      },
    },
    legend: { show: false },
  };

  return (
    <Card mb={6} bg={bgColor}>
      <HStack justifyContent={"space-between"} >
        <Text fontSize="xl" fontWeight="bold" p={4} color={textColor}>
        Production Activity
        </Text>
        <Stack alignItems={"center"} justifyContent={"center"} >
          <Button onClick={() => setShow(!show)} variant={"ghost"}>
            {show ? <TriangleUpIcon /> : <TriangleDownIcon />}
          </Button>
        </Stack>
      </HStack>

      <Collapse in={show} animateOpacity>
        <ApexChart
          options={options}
          series={series}
          type="heatmap"
          height={isSmallScreen ? 150 : isMediumScreen ? 250 : 350}
        />
      </Collapse>
    </Card>
  );
};

export default ProductionHeatmapApex;
